import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
// material
import { alpha, useTheme, styled } from '@mui/material/styles';
import { Box, List, Collapse, Typography, ListItemButton, ListItemIcon } from '@mui/material';
import { ChevronDown, ChevronRight } from 'lucide-react';

// ----------------------------------------------------------------------

const NavItemRoot = styled((props) => <ListItemButton disableGutters {...props} />)(({ theme }) => ({
  fontSize: '0.8125rem',
  height: 38,
  position: 'relative',
  textTransform: 'capitalize',
  color: theme.palette.text.secondary,
  borderRadius: 9,
  fontWeight: 500,
  paddingLeft: theme.spacing(1.25),
  paddingRight: theme.spacing(1.25),
  marginBottom: 2,
  transition: theme.transitions.create(['background-color', 'color', 'box-shadow'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    backgroundColor: alpha(theme.palette.text.primary, 0.04),
    color: theme.palette.text.primary,
  },
}));

const NavIconWrap = styled(ListItemIcon)(({ theme }) => ({
  width: 28,
  height: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 7,
  flexShrink: 0,
  marginRight: theme.spacing(1),
  minWidth: 'unset',
  color: 'inherit',
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.shorter,
  }),
}));

// ── Section label ────────────────────────────────────────────────────────
const SectionLabel = styled(Typography)(({ theme }) => ({
  ...theme.typography.overline,
  fontSize: '0.625rem',
  fontWeight: 700,
  color: theme.palette.text.disabled,
  letterSpacing: '0.08em',
  paddingLeft: theme.spacing(1.75),
  paddingRight: theme.spacing(1.75),
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(0.5),
  display: 'block',
}));

// ----------------------------------------------------------------------

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func,
};

function NavItem({ item, active }) {
  const theme = useTheme();
  const validChildren = Array.isArray(item?.children) ? item.children.filter(Boolean) : [];
  const hasChildren = Boolean(item?.children);
  let isActiveRoot = false;
  if (item) {
    isActiveRoot = hasChildren
      ? validChildren.some((c) => active(c.path, true))
      : active(item.path, false);
  }
  const [open, setOpen] = useState(isActiveRoot);

  useEffect(() => {
    if (isActiveRoot) {
      setOpen(true);
    }
  }, [isActiveRoot]);

  if (!item) return null;

  const { title, path, icon, info, children } = item;

  const activeStyles = {
    color: theme.palette.primary.main,
    fontWeight: 600,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      width: 3,
      height: 18,
      borderRadius: '0 3px 3px 0',
      backgroundColor: theme.palette.primary.main,
    },
  };

  const activeIconStyles = {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
  };

  if (children) {
    return (
      <>
        <NavItemRoot onClick={() => setOpen((prev) => !prev)} sx={{ ...(isActiveRoot && activeStyles) }}>
          <NavIconWrap sx={{ ...(isActiveRoot && activeIconStyles) }}>{icon}</NavIconWrap>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="body2"
              fontWeight={isActiveRoot ? 600 : 500}
              sx={{ color: 'inherit', fontSize: '0.8125rem', lineHeight: 1.2 }}
            >
              {title}
            </Typography>
          </Box>
          {info && info}
          {open ? (
            <ChevronDown size={14} style={{ flexShrink: 0, opacity: 0.6 }} />
          ) : (
            <ChevronRight size={14} style={{ flexShrink: 0, opacity: 0.6 }} />
          )}
        </NavItemRoot>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 1.5, my: 0.25 }}>
            {validChildren.map((child, index) => {
              if (!child) return null;
              const isActiveSub = active(child.path, true);
              return (
                <NavItemRoot
                  key={child.title || child.path || index}
                  component={RouterLink}
                  to={child.path}
                  end
                  sx={{
                    height: 32,
                    fontSize: '0.78rem',
                    borderRadius: 8,
                    mb: '2px',
                    ...(isActiveSub && {
                      color: 'primary.main',
                      fontWeight: 600,
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                    }),
                  }}
                >
                  <Box
                    sx={{
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      bgcolor: isActiveSub ? 'primary.main' : 'text.disabled',
                      ml: 1,
                      mr: 1.5,
                      flexShrink: 0,
                      transition: 'background-color 0.2s, transform 0.2s',
                      transform: isActiveSub ? 'scale(1.4)' : 'scale(1)',
                    }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={isActiveSub ? 600 : 400}
                    sx={{ color: 'inherit', fontSize: '0.78rem', flexGrow: 1 }}
                  >
                    {child.title}
                  </Typography>
                  {child.info && child.info}
                </NavItemRoot>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <NavItemRoot component={RouterLink} to={path} sx={{ ...(isActiveRoot && activeStyles) }}>
      <NavIconWrap sx={{ ...(isActiveRoot && activeIconStyles) }}>{icon}</NavIconWrap>
      <Box sx={{ flexGrow: 1 }}>
        <Typography
          variant="body2"
          fontWeight={isActiveRoot ? 600 : 500}
          sx={{ color: 'inherit', fontSize: '0.8125rem', lineHeight: 1.2 }}
        >
          {title}
        </Typography>
      </Box>
      {info && info}
    </NavItemRoot>
  );
}

// ── NavSection ───────────────────────────────────────────────────────────

NavSection.propTypes = {
  navConfig: PropTypes.array,
};

export default function NavSection({ navConfig, ...other }) {
  const { pathname } = useLocation();
  const match = (path, exact = false) =>
    path ? !!matchPath({ path, end: exact }, pathname) : false;
  const items = Array.isArray(navConfig) ? navConfig.filter(Boolean) : [];

  return (
    <Box {...other}>
      <SectionLabel>Main Menu</SectionLabel>
      <List disablePadding sx={{ px: 1.5 }}>
        {items.map((item, index) => (
          <NavItem key={item?.title || item?.path || index} item={item} active={match} />
        ))}
      </List>
    </Box>
  );
}
